import { useState, useCallback } from "react"
import type { ReactNode } from "react"
import type { EmblaCarouselType } from "embla-carousel"
import { CarouselContainer } from "../../atoms/CarouselContainer"
import type {
  CarouselContainerProps,
  ResponsivePeek
} from "../../atoms/CarouselContainer"
import type {
  ResponsiveArrows,
  ResponsivePaginationDots
} from "../../atoms/CarouselContainer/CarouselContainer"
import ArrowRight from "~/icons/ArrowRight"

/** Parse a responsive boolean prop into mobile/desktop flags */
function parseResponsiveBool(
  value: boolean | { mobile?: boolean; desktop?: boolean },
  defaultValue = false
): { mobile: boolean; desktop: boolean } {
  if (typeof value === "boolean") return { mobile: value, desktop: value }
  return {
    mobile: value.mobile ?? defaultValue,
    desktop: value.desktop ?? defaultValue
  }
}

/**
 * Returns a Tailwind class string that shows an element only at the
 * requested breakpoints.  Uses the given `display` value (e.g. "flex")
 * so it pairs naturally with the element's existing layout.
 * Returns `null` when the element should not be rendered at all.
 */
function responsiveDisplayClass(
  mobile: boolean,
  desktop: boolean,
  display: string = "flex"
): string | null {
  if (mobile && desktop) return display
  if (mobile) return `${display} md:hidden`
  if (desktop) return `hidden md:${display}`
  return null
}

export interface CarouselProps {
  children: ReactNode
  /** Whether to show navigation arrows */
  arrows?: ResponsiveArrows
  /** Position of the navigation arrows */
  arrowPosition?: "sides" | "bottom" | "top-right"
  /** Size of the navigation arrow buttons */
  arrowSize?: "default" | "small"
  /** Whether to show pagination dots */
  paginationDots?: ResponsivePaginationDots
  /** Whether pagination dots overlay the carousel content */
  paginationDotsOverlay?: boolean
  /** Number of slides to show at once (desktop) */
  slidesToShow?: CarouselContainerProps["slidesToShow"]
  /** Amount of the next slide to peek */
  peek?: ResponsivePeek
  /** Gap between slides in pixels */
  gap?: number
  /** Callback when embla is initialized */
  onInit?: (embla: EmblaCarouselType) => void
  /** Optional additional class names */
  className?: string
}

const ARROW_BUTTON_BASE =
  "flex items-center justify-center rounded-full border-2 bg-white transition-all ease-in-out disabled:opacity-0 disabled:pointer-events-none active:scale-95"

const ARROW_SIZE_CLASSES = {
  default: { button: "w-10 h-10", icon: "w-5 h-5" },
  small: { button: "w-6 h-6", icon: "w-3 h-3" }
}

const Carousel = ({
  children,
  arrows = true,
  arrowPosition = "sides",
  arrowSize = "default",
  paginationDots = false,
  paginationDotsOverlay = false,
  slidesToShow,
  peek,
  gap,
  onInit,
  className
}: CarouselProps) => {
  const { button: btnSizeClass, icon: iconSizeClass } =
    ARROW_SIZE_CLASSES[arrowSize]
  const arrowButtonClass = `${ARROW_BUTTON_BASE} ${btnSizeClass}`
  const { mobile: showArrowsMobile, desktop: showArrowsDesktop } =
    parseResponsiveBool(arrows)
  const { mobile: showDotsMobile, desktop: showDotsDesktop } =
    parseResponsiveBool(paginationDots)

  const showAnyArrows = showArrowsMobile || showArrowsDesktop
  const showAnyDots = showDotsMobile || showDotsDesktop

  const arrowsDisplay = responsiveDisplayClass(
    showArrowsMobile,
    showArrowsDesktop
  )
  const dotsDisplay = responsiveDisplayClass(showDotsMobile, showDotsDesktop)

  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [selectedSnap, setSelectedSnap] = useState(0)

  const updateScrollState = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
    setSelectedSnap(api.selectedScrollSnap())
  }, [])

  const updateScrollSnaps = useCallback((api: EmblaCarouselType) => {
    setScrollSnaps(api.scrollSnapList())
  }, [])

  const handleInit = useCallback(
    (api: EmblaCarouselType) => {
      setEmblaApi(api)
      updateScrollState(api)
      updateScrollSnaps(api)
      api.on("select", () => updateScrollState(api))
      api.on("reInit", () => {
        updateScrollState(api)
        updateScrollSnaps(api)
      })
      if (onInit) {
        onInit(api)
      }
    },
    [onInit, updateScrollState, updateScrollSnaps]
  )

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index)
    },
    [emblaApi]
  )

  const dotsEl =
    dotsDisplay && scrollSnaps.length > 1 ? (
      <div
        className={`${dotsDisplay} items-center justify-center gap-2${paginationDotsOverlay ? " absolute bottom-3 left-0 right-0" : " mt-2"}`}
      >
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Go to page ${index + 1}`}
            onClick={() => scrollTo(index)}
            className={`rounded-full transition-all ease-in-out focus:outline-none${
              index === selectedSnap
                ? " w-2 h-2 bg-camp-primary"
                : " w-2 h-2 bg-transparent border"
            }`}
          />
        ))}
      </div>
    ) : null

  const container = (
    <CarouselContainer
      slidesToShow={slidesToShow}
      peek={peek}
      gap={gap}
      onInit={handleInit}
      className={className}
    >
      {children}
    </CarouselContainer>
  )

  if (!showAnyArrows) {
    if (showAnyDots) {
      if (paginationDotsOverlay) {
        return (
          <div className="relative">
            {container}
            {dotsEl}
          </div>
        )
      }
      return (
        <div>
          {container}
          {dotsEl}
        </div>
      )
    }
    return (
      <CarouselContainer
        slidesToShow={slidesToShow}
        peek={peek}
        gap={gap}
        onInit={onInit}
        className={className}
      >
        {children}
      </CarouselContainer>
    )
  }

  if (arrowPosition === "top-right") {
    return (
      <div className="relative">
        <div
          className={`absolute top-8 right-0 -translate-y-full pb-3 flex gap-2 z-10${arrowsDisplay ? ` ${arrowsDisplay}` : ""}`}
        >
          <button
            type="button"
            aria-label="Previous slide"
            className={arrowButtonClass}
            onClick={scrollPrev}
            disabled={!canScrollPrev}
          >
            <ArrowRight className={`${iconSizeClass} rotate-180`} />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            className={arrowButtonClass}
            onClick={scrollNext}
            disabled={!canScrollNext}
          >
            <ArrowRight className={iconSizeClass} />
          </button>
        </div>
        <div className={paginationDotsOverlay ? "relative" : undefined}>
          {container}
          {paginationDotsOverlay && dotsEl}
        </div>
        {!paginationDotsOverlay && dotsEl}
      </div>
    )
  }

  if (arrowPosition === "bottom") {
    return (
      <div className="flex flex-col">
        <div className={paginationDotsOverlay ? "relative" : undefined}>
          {container}
          {paginationDotsOverlay && dotsEl}
        </div>
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            type="button"
            aria-label="Previous slide"
            className={`${arrowsDisplay} ${arrowButtonClass}`}
            onClick={scrollPrev}
            disabled={!canScrollPrev}
          >
            <ArrowRight className={`${iconSizeClass} rotate-180`} />
          </button>
          {!paginationDotsOverlay && dotsEl}
          <button
            type="button"
            aria-label="Next slide"
            className={`${arrowsDisplay} ${arrowButtonClass}`}
            onClick={scrollNext}
            disabled={!canScrollNext}
          >
            <ArrowRight className={iconSizeClass} />
          </button>
        </div>
      </div>
    )
  }

  // arrowPosition === "sides" (default)
  return (
    <div className="flex flex-col">
      <div className="relative flex items-center">
        <div
          className={`absolute left-0 -translate-x-full pr-2 z-10${arrowsDisplay ? ` ${arrowsDisplay}` : ""}`}
        >
          <button
            type="button"
            aria-label="Previous slide"
            className={arrowButtonClass}
            onClick={scrollPrev}
            disabled={!canScrollPrev}
          >
            <ArrowRight className={`${iconSizeClass} rotate-180`} />
          </button>
        </div>
        <div className={paginationDotsOverlay ? "relative flex-1" : "flex-1"}>
          {container}
          {paginationDotsOverlay && dotsEl}
        </div>
        <div
          className={`absolute right-0 translate-x-full pl-2 z-10${arrowsDisplay ? ` ${arrowsDisplay}` : ""}`}
        >
          <button
            type="button"
            aria-label="Next slide"
            className={arrowButtonClass}
            onClick={scrollNext}
            disabled={!canScrollNext}
          >
            <ArrowRight className={iconSizeClass} />
          </button>
        </div>
      </div>
      {!paginationDotsOverlay && dotsEl}
    </div>
  )
}

Carousel.displayName = "Carousel"

export { Carousel }
export default Carousel
